import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePrixPlans, formatFcfa } from '../lib/tarifs';
import {
  BookOpen, Users, Award, Clock, Star, Play, ArrowRight,
  Shield, Zap, MessageSquare, BarChart2, Bell, Settings,
  CheckCircle, Lock, LogIn, UserPlus, Search,
  ChevronDown, ChevronRight, Home, Trophy,
  Brain, CreditCard, FileText, Eye, Download,
  Target, TrendingUp, Globe, Headphones, List,
  HelpCircle, AlertCircle, Info, ExternalLink,
  LayoutDashboard, BookMarked, Layers, Briefcase,
  ShoppingBag, Lightbulb, Leaf, Map, Package,
  DollarSign, PieChart, Users2, Wrench, Cpu
} from 'lucide-react';

/* ─── Table of contents ──────────────────────────────────────────── */
const TOC = [
  { id: 'intro',         label: 'La plateforme',            icon: Home },
  { id: 'modules',       label: 'Tous les modules',         icon: Layers },
  { id: 'getting-started', label: 'Démarrage rapide',       icon: Zap },
  { id: 'mooc',          label: 'MOOC — Formation',         icon: BookOpen },
  { id: 'kit360',        label: 'Kit360° — Boîte à outils', icon: Wrench },
  { id: 'gestion',       label: 'Gestion',                  icon: BarChart2 },
  { id: 'agrogestion',   label: 'AgroGestion',              icon: Leaf },
  { id: 'business-plan', label: 'Business Plan',            icon: FileText },
  { id: 'bourse-idees',  label: 'Bourse d\'Idées',          icon: Lightbulb },
  { id: 'marketplace',   label: 'Marketplace',              icon: ShoppingBag },
  { id: 'coaching',      label: 'Coaching',                 icon: Target },
  { id: 'transversal',   label: 'Fonctionnalités communes', icon: Cpu },
  { id: 'subscription',  label: 'Abonnements',              icon: CreditCard },
  { id: 'profile',       label: 'Profil & Paramètres',      icon: Settings },
  { id: 'security',      label: 'Sécurité',                 icon: Lock },
  { id: 'faq',           label: 'FAQ',                      icon: HelpCircle },
];

/* ─── Reusable components ────────────────────────────────────────── */

function SectionTitle({ id, icon: Icon, children, color = 'blue' }) {
  const grad = {
    blue:   'from-blue-600 to-indigo-600',
    purple: 'from-purple-600 to-pink-600',
    green:  'from-green-600 to-teal-600',
    orange: 'from-orange-500 to-red-500',
    yellow: 'from-yellow-500 to-orange-500',
    teal:   'from-teal-500 to-cyan-600',
    rose:   'from-rose-500 to-pink-600',
  };
  return (
    <div id={id} className="scroll-mt-24 mb-8 pt-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${grad[color]} text-white shadow-md`}>
          <Icon size={22} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{children}</h2>
      </div>
      <div className={`h-1 w-20 rounded-full bg-gradient-to-r ${grad[color]}`} />
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, color = 'blue' }) {
  const palette = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    red:    'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    teal:   'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
    rose:   'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
  };
  return (
    <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${palette[color]}`}>
        <Icon size={20} />
      </div>
      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function StepCard({ number, title, children, icon: Icon }) {
  return (
    <div className="flex gap-4 p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          {Icon && <Icon size={16} className="text-blue-500" />}
          <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function InfoBox({ type = 'info', children }) {
  const cfg = {
    info:    { bg: 'bg-blue-50 dark:bg-blue-900/20',    border: 'border-blue-200 dark:border-blue-800',    icon: Info,        text: 'text-blue-700 dark:text-blue-300'   },
    warning: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', icon: AlertCircle, text: 'text-yellow-700 dark:text-yellow-300' },
    success: { bg: 'bg-green-50 dark:bg-green-900/20',  border: 'border-green-200 dark:border-green-800',  icon: CheckCircle, text: 'text-green-700 dark:text-green-300'  },
    danger:  { bg: 'bg-red-50 dark:bg-red-900/20',      border: 'border-red-200 dark:border-red-800',      icon: Shield,      text: 'text-red-700 dark:text-red-300'     },
  };
  const { bg, border, icon: Icon, text } = cfg[type];
  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${bg} ${border} my-4`}>
      <Icon size={18} className={`flex-shrink-0 mt-0.5 ${text}`} />
      <div className={`text-sm leading-relaxed ${text}`}>{children}</div>
    </div>
  );
}

function PlanBadge({ plan }) {
  const cfg = {
    Gratuit:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    Pro:      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'Pro Max':'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg[plan]}`}>
      <CreditCard size={10} /> {plan}
    </span>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        <span className="font-medium text-gray-900 dark:text-white">{question}</span>
        {open
          ? <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
          : <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 pt-3 bg-white dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400 leading-relaxed border-t border-gray-100 dark:border-gray-700">
          {answer}
        </div>
      )}
    </div>
  );
}

/* ─── Illustrated diagrams ────────────────────────────────────────── */

function UnifiedPlatformDiagram() {
  const modules = [
    { icon: BookOpen,   label: 'MOOC',          color: 'bg-blue-500',   sub: 'Formation' },
    { icon: Wrench,     label: 'Kit360°',        color: 'bg-indigo-500', sub: '40+ outils' },
    { icon: BarChart2,  label: 'Gestion',        color: 'bg-green-500',  sub: 'Business' },
    { icon: Leaf,       label: 'AgroGestion',    color: 'bg-teal-500',   sub: 'Agriculture' },
    { icon: FileText,   label: 'Business Plan',  color: 'bg-purple-500', sub: 'Planification' },
    { icon: Lightbulb,  label: 'Bourse Idées',   color: 'bg-yellow-500', sub: 'Innovation' },
    { icon: ShoppingBag,label: 'Marketplace',    color: 'bg-orange-500', sub: 'Commerce' },
    { icon: Target,     label: 'Coaching',       color: 'bg-pink-500',   sub: 'Accompagnement' },
  ];
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900/50 dark:to-blue-950/30 rounded-2xl border border-blue-100 dark:border-blue-900">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-2xl font-black text-lg shadow-lg mb-2">
          <Globe size={20} /> Innovactive360°
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">Plateforme unifiée pour entrepreneurs</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {modules.map(({ icon: Icon, label, color, sub }) => (
          <div key={label} className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div className={`w-11 h-11 rounded-xl ${color} text-white flex items-center justify-center shadow`}>
              <Icon size={20} />
            </div>
            <p className="text-xs font-bold text-gray-800 dark:text-white text-center">{label}</p>
            <p className="text-xs text-gray-400 text-center">{sub}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs">
        {[
          { icon: Brain, label: 'IA intégrée' },
          { icon: Users2, label: 'Collaboration' },
          { icon: Globe, label: 'Multi-devise' },
          { icon: Shield, label: 'SSO sécurisé' },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
            <Icon size={12} className="text-blue-500" /> {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function Kit360Diagram() {
  const cats = [
    { label: 'Idéation',   color: 'bg-yellow-500', tools: ['Qualités entrepreneur', 'Analyse d\'idée', '8 Questions clés'] },
    { label: 'Stratégie',  color: 'bg-blue-500',   tools: ['Business Model Canvas', 'PESTEL', 'Forces de Porter', 'Matrice BCG'] },
    { label: 'Marketing',  color: 'bg-pink-500',   tools: ['Diagnostic 4P', 'Plan Marketing', 'Étude de marché', 'Prix psychologique'] },
    { label: 'Production', color: 'bg-green-500',  tools: ['Plan de production', 'Coût de revient', 'Évaluation fournisseurs'] },
    { label: 'RH',         color: 'bg-purple-500', tools: ['Fiche de poste', 'Recrutement', 'Contrats', 'Plan motivation'] },
    { label: 'Pilotage',   color: 'bg-orange-500', tools: ['Tableau de bord KPI', 'Plan trésorerie', 'Seuil rentabilité', 'Gantt'] },
    { label: 'Gestion',    color: 'bg-teal-500',   tools: ['Journal Grand Livre', 'Gestion stock', 'Évaluation risque'] },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 my-6">
      {cats.map(({ label, color, tools }) => (
        <div key={label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className={`${color} text-white px-4 py-2 text-sm font-bold`}>{label}</div>
          <ul className="p-3 space-y-1">
            {tools.map(t => (
              <li key={t} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <CheckCircle size={11} className="text-green-500 flex-shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function GestionModulesGrid({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
      {items.map(({ icon: Icon, label, color }) => (
        <div key={label} className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-medium
          ${color === 'blue'   ? 'bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900 dark:text-blue-300' :
            color === 'green'  ? 'bg-green-50 border-green-100 text-green-700 dark:bg-green-900/20 dark:border-green-900 dark:text-green-300' :
            color === 'purple' ? 'bg-purple-50 border-purple-100 text-purple-700 dark:bg-purple-900/20 dark:border-purple-900 dark:text-purple-300' :
            color === 'orange' ? 'bg-orange-50 border-orange-100 text-orange-700 dark:bg-orange-900/20 dark:border-orange-900 dark:text-orange-300' :
            color === 'teal'   ? 'bg-teal-50 border-teal-100 text-teal-700 dark:bg-teal-900/20 dark:border-teal-900 dark:text-teal-300' :
            'bg-gray-50 border-gray-100 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'}`}>
          <Icon size={15} className="flex-shrink-0" />
          {label}
        </div>
      ))}
    </div>
  );
}

function SubscriptionTable() {
  const prix = usePrixPlans();
  const mensuel = (plan) => (prix?.[plan]?.monthly ? `${formatFcfa(prix[plan].monthly)} / mois` : '');
  const plans = [
    {
      name: 'Gratuit', price: 'Gratuit', color: 'border-gray-300', badge: null,
      features: [
        'Test entrepreneurial',
        'Cours MOOC gratuits',
        '1 publication Bourse d\'Idées',
        'Accès limité Marketplace',
        '1 Business Plan basique',
        'Tableau de bord',
      ],
    },
    {
      name: 'Pro', price: mensuel('pro'), color: 'border-blue-400', badge: 'Populaire',
      features: [
        'Tout du plan Gratuit',
        'Tous les cours MOOC',
        'Gestion complète',
        'AgroGestion complète',
        'Kit360° (40+ outils)',
        '5 Business Plans',
        'Publications illimitées',
        'Coaching',
      ],
    },
    {
      name: 'Pro Max', price: mensuel('promax'), color: 'border-purple-400', badge: null,
      features: [
        'Tout du plan Pro',
        'Business Plans illimités',
        '10 collaborateurs',
        'Produits Marketplace illimités',
        'Analytics avancés',
        'Support prioritaire',
        'API access',
      ],
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
      {plans.map(({ name, price, color, badge, features }) => (
        <div key={name} className={`relative p-5 bg-white dark:bg-gray-800 rounded-2xl border-2 ${color} shadow-sm`}>
          {badge && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
              {badge}
            </span>
          )}
          <h4 className="font-bold text-gray-900 dark:text-white mb-1">{name}</h4>
          <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mb-4">{price}</p>
          <ul className="space-y-2">
            {features.map(f => (
              <li key={f} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                <CheckCircle size={12} className="text-green-500 flex-shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ModuleCard({ icon: Icon, label, color, plan, desc, path }) {
  const bg = {
    blue: 'from-blue-500 to-indigo-600', purple: 'from-purple-500 to-pink-600',
    green: 'from-green-500 to-teal-600', orange: 'from-orange-400 to-red-500',
    yellow: 'from-yellow-400 to-orange-500', teal: 'from-teal-500 to-cyan-600',
    pink: 'from-pink-500 to-rose-600', indigo: 'from-indigo-500 to-blue-600',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden hover:shadow-md transition-all hover:-translate-y-0.5">
      <div className={`bg-gradient-to-br ${bg[color]} p-4 flex items-center gap-3`}>
        <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-white">
          <Icon size={20} />
        </div>
        <div>
          <p className="font-bold text-white text-sm">{label}</p>
          <PlanBadge plan={plan} />
        </div>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{desc}</p>
        {path && (
          <Link to={path} className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium">
            Accéder <ExternalLink size={11} />
          </Link>
        )}
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */

export default function Documentation() {
  const [activeSection, setActiveSection] = useState('intro');
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY + 120;
      const sections = TOC.map(t => document.getElementById(t.id)).filter(Boolean);
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetTop <= scrollY) {
          setActiveSection(TOC[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTocOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">

      {/* ── Hero ────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-4 py-1.5 text-sm font-medium mb-5">
                <BookOpen size={15} /> Guide utilisateur officiel
              </div>
              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                Documentation<br />
                <span className="text-blue-200">Innovactive360°</span>
              </h1>
              <p className="text-blue-100 text-lg leading-relaxed max-w-xl mb-8">
                La plateforme tout-en-un pour entrepreneurs — formation, gestion, outils stratégiques,
                marketplace et accompagnement réunis dans un seul espace.
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => scrollTo('getting-started')}
                  className="flex items-center gap-2 bg-white text-blue-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-md">
                  <Zap size={16} /> Démarrer
                </button>
                <button onClick={() => scrollTo('modules')}
                  className="flex items-center gap-2 border border-white/40 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-white/10 transition-colors">
                  <Layers size={16} /> Voir les modules
                </button>
              </div>
            </div>
            {/* Stats illustration */}
            <div className="hidden md:block flex-shrink-0 w-72">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="space-y-3">
                  {[
                    { icon: Layers,    label: 'Modules',       val: '8',     color: 'bg-blue-500' },
                    { icon: Wrench,    label: 'Outils Kit360°', val: '40+',  color: 'bg-indigo-500' },
                    { icon: BookOpen,  label: 'Cours MOOC',    val: '500+',  color: 'bg-purple-500' },
                    { icon: ShoppingBag, label: 'Catégories Marketplace', val: '20+', color: 'bg-orange-500' },
                    { icon: Users,     label: 'Entrepreneurs', val: '10K+',  color: 'bg-green-500' },
                  ].map(({ icon: Icon, label, val, color }) => (
                    <div key={label} className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center`}>
                          <Icon size={13} />
                        </div>
                        <span className="text-sm text-blue-100">{label}</span>
                      </div>
                      <span className="text-sm font-bold text-white">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-8 items-start">

          {/* ── Sidebar TOC (desktop) ── */}
          <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-20">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-100 dark:border-gray-700">
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Table des matières
                </p>
              </div>
              <nav className="py-2 max-h-[70vh] overflow-y-auto">
                {TOC.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => scrollTo(id)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors text-left
                      ${activeSection === id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'}`}>
                    <Icon size={14} className="flex-shrink-0" /> {label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* ── Mobile TOC ── */}
          <div className="lg:hidden w-full">
            <button onClick={() => setTocOpen(!tocOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <List size={16} /> Table des matières
              </div>
              {tocOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {tocOpen && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6 overflow-hidden">
                {TOC.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => scrollTo(id)}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 text-left border-b border-gray-50 dark:border-gray-700 last:border-0">
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Main content ── */}
          <main className="flex-1 min-w-0 space-y-14">

            {/* ══ INTRODUCTION ══════════════════════════════════════ */}
            <section>
              <SectionTitle id="intro" icon={Globe} color="blue">La plateforme Innovactive360°</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                <strong className="text-gray-900 dark:text-white">Innovactive360°</strong> est une plateforme SaaS
                tout-en-un conçue pour accompagner les entrepreneurs à chaque étape de leur parcours — de l'idéation
                à la gestion opérationnelle, en passant par la formation, la planification stratégique et la mise en
                relation avec d'autres acteurs de l'écosystème.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Huit modules spécialisés sont accessibles depuis un espace unique, avec une authentification partagée,
                des outils d'IA intégrés et un tableau de bord centralisé.
              </p>
              <UnifiedPlatformDiagram />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <FeatureCard icon={Layers}    title="8 modules intégrés"         desc="MOOC, Kit360°, Gestion, AgroGestion, Business Plan, Bourse d'Idées, Marketplace, Coaching."      color="blue"   />
                <FeatureCard icon={Brain}     title="IA dans chaque module"      desc="Assistants IA dédiés pour la formation, la gestion, l'agro et le kit stratégique."               color="purple" />
                <FeatureCard icon={Users2}    title="Collaboration multi-utilisateurs" desc="Invitez des collaborateurs sur vos espaces Gestion et AgroGestion avec rôles distincts."    color="green"  />
                <FeatureCard icon={Globe}     title="Multi-devise"               desc="EUR, XOF, USD — travaillez dans la devise adaptée à votre marché."                                color="orange" />
                <FeatureCard icon={Shield}    title="SSO sécurisé"               desc="Une seule connexion pour accéder à tous les modules, avec des tokens renouvelés automatiquement." color="teal"   />
                <FeatureCard icon={BarChart2} title="Tableau de bord unifié"     desc="Vue consolidée de votre activité sur tous les modules depuis un seul endroit."                    color="yellow" />
              </div>
            </section>

            {/* ══ MODULES OVERVIEW ══════════════════════════════════ */}
            <section>
              <SectionTitle id="modules" icon={Layers} color="blue">Vue d'ensemble de tous les modules</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Chaque module répond à un besoin spécifique. Votre abonnement détermine les modules accessibles.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModuleCard icon={BookOpen}    label="MOOC"            color="blue"   plan="Gratuit" path="/mooc"
                  desc="Plateforme de formation en ligne avec 500+ cours, évaluations, certificats et gamification." />
                <ModuleCard icon={Wrench}      label="Kit360°"         color="indigo" plan="Pro"
                  desc="40+ outils stratégiques organisés en 7 catégories : idéation, stratégie, marketing, RH, pilotage…" />
                <ModuleCard icon={BarChart2}   label="Gestion"         color="green"  plan="Pro"
                  desc="Gestion complète d'entreprise : ventes, stocks, clients, personnel, trésorerie, rentabilité." />
                <ModuleCard icon={Leaf}        label="AgroGestion"     color="teal"   plan="Pro"
                  desc="Solution agricole spécialisée : parcelles, cultures, élevage, récoltes, transformation, trésorerie." />
                <ModuleCard icon={FileText}    label="Business Plan"   color="purple" plan="Pro"
                  desc="Créez et gérez vos business plans professionnels avec des modèles structurés et exportables." />
                <ModuleCard icon={Lightbulb}   label="Bourse d'Idées"  color="yellow" plan="Gratuit"
                  desc="Publiez vos idées de projet, trouvez des partenaires, investisseurs ou collaborateurs." />
                <ModuleCard icon={ShoppingBag} label="Marketplace"     color="orange" plan="Gratuit"
                  desc="Achetez et vendez des services, formations, templates et produits dans 20+ catégories." />
                <ModuleCard icon={Target}      label="Coaching"        color="pink"   plan="Pro"
                  desc="Accompagnement individualisé par des coachs experts avec plans, séances et suivi d'objectifs." />
              </div>
              <InfoBox type="info">
                Certains modules s'ouvrent dans un onglet séparé via SSO automatique — votre session est transmise de manière sécurisée, sans ressaisie de mot de passe.
              </InfoBox>
            </section>

            {/* ══ GETTING STARTED ═══════════════════════════════════ */}
            <section>
              <SectionTitle id="getting-started" icon={Zap} color="blue">Démarrage rapide</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Créez votre compte et accédez à votre premier module en moins de 5 minutes.
              </p>
              <div className="space-y-4">
                <StepCard number="1" title="Créer un compte" icon={UserPlus}>
                  Rendez-vous sur <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">/register</Link>.
                  Renseignez votre prénom, nom, email et un mot de passe sécurisé. Validez le CAPTCHA de sécurité,
                  puis cliquez sur <strong>«&nbsp;Créer mon compte&nbsp;»</strong>.
                  Le compte gratuit est immédiatement actif.
                </StepCard>
                <StepCard number="2" title="Se connecter" icon={LogIn}>
                  Sur la page <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">/login</Link>,
                  entrez votre email et mot de passe. La connexion SSO est disponible si votre organisation l'a configurée.
                  Une seule connexion vous donne accès à tous les modules de la plateforme.
                </StepCard>
                <StepCard number="3" title="Explorer le tableau de bord" icon={LayoutDashboard}>
                  Après connexion, le tableau de bord centralisé affiche un raccourci vers tous vos modules actifs,
                  vos dernières activités et vos statistiques globales.
                </StepCard>
                <StepCard number="4" title="Choisir un module" icon={Layers}>
                  Depuis le menu de navigation, sélectionnez le module qui correspond à votre besoin du moment.
                  Les modules accessibles dépendent de votre plan d'abonnement.
                </StepCard>
                <StepCard number="5" title="Passer au plan Pro si besoin" icon={CreditCard}>
                  Le plan gratuit donne accès aux fonctionnalités essentielles. Pour débloquer Gestion, AgroGestion,
                  Kit360° et tous les cours MOOC, optez pour le plan <strong>Pro</strong> (tarifs sur la page Abonnement).
                </StepCard>
              </div>
            </section>

            {/* ══ MOOC ══════════════════════════════════════════════ */}
            <section>
              <SectionTitle id="mooc" icon={BookOpen} color="blue">MOOC — Formation en ligne</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Gratuit" />
                <span className="text-xs text-gray-400">(cours premium avec plan Pro)</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Le module MOOC propose une plateforme d'apprentissage complète avec des cours conçus pour
                les entrepreneurs, les professionnels et les porteurs de projets.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <FeatureCard icon={BookOpen}    title="500+ cours"               desc="Développement, business, design, marketing, agriculture, finance, entrepreneuriat et plus." color="blue"   />
                <FeatureCard icon={Play}        title="Leçons vidéo & texte"     desc="Contenu multiformat avec lecteur vidéo intégré, chapitres et ressources téléchargeables."  color="purple" />
                <FeatureCard icon={CheckCircle} title="Évaluations & quiz"       desc="Quiz chronométrés avec score de passage, tentatives multiples et corrections détaillées."  color="green"  />
                <FeatureCard icon={Award}       title="Certificats vérifiables"  desc="Certificats numériques partageables sur LinkedIn, vérifiables publiquement via /verify."  color="yellow" />
                <FeatureCard icon={Trophy}      title="Gamification"             desc="Badges, points XP, streak quotidien et classement pour gamifier votre apprentissage."     color="orange" />
                <FeatureCard icon={MessageSquare} title="Chat & communauté"      desc="Canaux de discussion par cours, statut en ligne, questions à l'instructeur."              color="teal"   />
                <FeatureCard icon={Brain}       title="Assistant IA"             desc="IA disponible 24h/24 pour expliquer des concepts et recommander des cours."               color="purple" />
                <FeatureCard icon={Layers}      title="Programmes"               desc="Parcours structurés multi-cours pour atteindre un objectif professionnel complet."         color="blue"   />
              </div>

              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Parcours d'apprentissage</h3>
              <div className="flex items-center gap-2 overflow-x-auto pb-2 my-4">
                {[
                  { icon: Search, label: 'Chercher', color: 'bg-blue-500' },
                  { icon: BookMarked, label: "S'inscrire", color: 'bg-indigo-500' },
                  { icon: Play, label: 'Apprendre', color: 'bg-purple-500' },
                  { icon: CheckCircle, label: 'Évaluation', color: 'bg-pink-500' },
                  { icon: Award, label: 'Certificat', color: 'bg-green-500' },
                  { icon: Trophy, label: 'Badge XP', color: 'bg-yellow-500' },
                ].map(({ icon: Icon, label, color }, i, arr) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div className={`w-11 h-11 rounded-xl ${color} text-white flex items-center justify-center shadow`}>
                        <Icon size={18} />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-center w-16">{label}</span>
                    </div>
                    {i < arr.length - 1 && <ArrowRight size={14} className="text-gray-300 flex-shrink-0 mb-4" />}
                  </React.Fragment>
                ))}
              </div>

              <h3 className="font-bold text-gray-900 dark:text-white mb-3 mt-6">Système de badges</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { label: 'Premier Pas',   desc: 'Terminer 1 cours',            color: 'bg-blue-500' },
                  { label: 'Streak 7j',     desc: '7 jours consécutifs',         color: 'bg-yellow-500' },
                  { label: 'Communauté',    desc: '10 messages envoyés',         color: 'bg-green-500' },
                  { label: 'Expert',        desc: '90%+ à une évaluation',       color: 'bg-purple-500' },
                  { label: 'Explorateur',   desc: '5 catégories explorées',      color: 'bg-orange-500' },
                  { label: 'Milestone',     desc: '10 cours terminés',           color: 'bg-pink-500' },
                ].map(({ label, desc, color }) => (
                  <div key={label} className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <div className={`w-8 h-8 rounded-lg ${color} text-white flex items-center justify-center flex-shrink-0`}>
                      <Star size={14} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800 dark:text-white">{label}</p>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ══ KIT360 ════════════════════════════════════════════ */}
            <section>
              <SectionTitle id="kit360" icon={Wrench} color="purple">Kit360° — Boîte à outils stratégique</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Pro" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Le Kit360° regroupe plus de 40 outils prêts à l'emploi organisés en 7 grandes catégories.
                Chaque outil est conçu pour une étape précise du parcours entrepreneurial, de l'idéation au pilotage.
              </p>
              <Kit360Diagram />
              <InfoBox type="info">
                Chaque outil du Kit360° est utilisable de manière indépendante. Vos données sont sauvegardées automatiquement et accessibles depuis n'importe quel appareil.
              </InfoBox>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <FeatureCard icon={Lightbulb} title="Idéation & Analyse"  desc="Évaluez la viabilité de vos idées, identifiez vos forces d'entrepreneur et posez les bonnes questions dès le départ." color="yellow" />
                <FeatureCard icon={Map}       title="Outils stratégiques" desc="Canvas, PESTEL, Porter, BCG — tous les frameworks stratégiques clés en version interactive." color="blue"   />
                <FeatureCard icon={Target}    title="Marketing opérationnel" desc="Diagnostics 4P, plan marketing complet, analyse concurrentielle et étude de marché guidée." color="pink"   />
                <FeatureCard icon={PieChart}  title="Pilotage financier"   desc="Tableau de bord KPI, plan de trésorerie, seuil de rentabilité et évaluation financière." color="green"  />
              </div>
            </section>

            {/* ══ GESTION ═══════════════════════════════════════════ */}
            <section>
              <SectionTitle id="gestion" icon={BarChart2} color="green">Gestion — Gestion d'entreprise</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Pro" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Le module Gestion est un ERP simplifié qui couvre toutes les dimensions opérationnelles
                d'une entreprise : commercial, stocks, comptabilité, RH et trésorerie.
              </p>
              <GestionModulesGrid items={[
                { icon: TrendingUp, label: 'Ventes & Facturation',    color: 'blue' },
                { icon: Package,    label: 'Stocks & Inventaire',     color: 'green' },
                { icon: Users,      label: 'Clients (CRM)',           color: 'purple' },
                { icon: Briefcase,  label: 'Personnel & RH',          color: 'orange' },
                { icon: DollarSign, label: 'Trésorerie',              color: 'teal' },
                { icon: BarChart2,  label: 'Rentabilité',             color: 'blue' },
                { icon: FileText,   label: 'Journal comptable',       color: 'green' },
                { icon: PieChart,   label: 'Compte d\'exploitation',  color: 'purple' },
                { icon: Map,        label: 'Dépôts-ventes',           color: 'orange' },
                { icon: Settings,   label: 'Production',              color: 'teal' },
                { icon: Users2,     label: 'Collaboration',           color: 'blue' },
                { icon: Globe,      label: 'Multi-devise',            color: 'green' },
              ]} />
              <InfoBox type="info">
                La fonctionnalité <strong>Collaboration</strong> permet à l'owner du compte d'inviter des collaborateurs
                via un lien d'invitation. Les collaborateurs ont un accès en lecture seule par défaut.
              </InfoBox>
            </section>

            {/* ══ AGROGESTION ═══════════════════════════════════════ */}
            <section>
              <SectionTitle id="agrogestion" icon={Leaf} color="teal">AgroGestion — Gestion agricole</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Pro" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                L'AgroGestion est une solution dédiée aux entrepreneurs agricoles. Elle intègre toutes
                les spécificités du secteur : gestion des parcelles, suivi des cultures, élevage et transformation.
              </p>
              <GestionModulesGrid items={[
                { icon: Map,        label: 'Parcelles & Champs',      color: 'teal' },
                { icon: Leaf,       label: 'Cultures',                color: 'green' },
                { icon: Package,    label: 'Élevage',                 color: 'orange' },
                { icon: Wrench,     label: 'Intrants & Fournitures',  color: 'blue' },
                { icon: Star,       label: 'Récoltes',                color: 'yellow' },
                { icon: Settings,   label: 'Productions animales',    color: 'purple' },
                { icon: Layers,     label: 'Transformation',          color: 'teal' },
                { icon: ShoppingBag,label: 'Approvisionnement',       color: 'green' },
                { icon: DollarSign, label: 'Trésorerie agro',         color: 'orange' },
                { icon: PieChart,   label: 'Compte d\'exploitation',  color: 'blue' },
                { icon: Users2,     label: 'Collaboration',           color: 'teal' },
                { icon: Globe,      label: 'Multi-devise',            color: 'green' },
              ]} />
              <InfoBox type="success">
                L'AgroGestion dispose d'un assistant IA spécialisé agriculture pour vous conseiller sur
                les cultures, les itinéraires techniques et l'optimisation de vos rendements.
              </InfoBox>
            </section>

            {/* ══ BUSINESS PLAN ════════════════════════════════════ */}
            <section>
              <SectionTitle id="business-plan" icon={FileText} color="purple">Business Plan</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Gratuit" />
                <span className="text-xs text-gray-400">(1 BP basique) ·</span>
                <PlanBadge plan="Pro" />
                <span className="text-xs text-gray-400">(5 BP) ·</span>
                <PlanBadge plan="Pro Max" />
                <span className="text-xs text-gray-400">(illimité)</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Le module Business Plan vous accompagne dans la structuration et la rédaction de vos plans
                d'affaires professionnels. Il s'ouvre dans un espace dédié via votre session sécurisée.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FeatureCard icon={FileText}  title="Modèles structurés"    desc="Suivez un canevas guidé : résumé, étude de marché, stratégie, plan financier, équipe."   color="purple" />
                <FeatureCard icon={Download}  title="Export professionnel"  desc="Exportez votre BP en PDF ou Word pour le présenter à des investisseurs ou partenaires."  color="blue"   />
                <FeatureCard icon={Brain}     title="Assistance IA"         desc="L'IA vous aide à rédiger chaque section, reformuler et corriger votre contenu."           color="green"  />
                <FeatureCard icon={Eye}       title="Prévisualisation"      desc="Visualisez le rendu final de votre business plan avant export."                           color="orange" />
              </div>
              <InfoBox type="info">
                Le module s'ouvre dans un nouvel onglet. Si votre navigateur bloque les pop-ups, autorisez-les pour innovactive360.cloud afin d'accéder au module sans friction.
              </InfoBox>
            </section>

            {/* ══ BOURSE IDÉES ══════════════════════════════════════ */}
            <section>
              <SectionTitle id="bourse-idees" icon={Lightbulb} color="yellow">Bourse d'Idées</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Gratuit" />
                <span className="text-xs text-gray-400">(1 publication) ·</span>
                <PlanBadge plan="Pro" />
                <span className="text-xs text-gray-400">(illimité)</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                La Bourse d'Idées est une place de marché d'idées entrepreneuriales. Publiez votre projet,
                découvrez ceux des autres et entrez en contact avec des partenaires potentiels ou des investisseurs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FeatureCard icon={Lightbulb}   title="Publier une idée"     desc="Présentez votre idée de projet : secteur, stade de développement, besoins, vision."     color="yellow" />
                <FeatureCard icon={Search}      title="Explorer les projets" desc="Parcourez les idées publiées, filtrez par secteur, stade ou type de collaboration."     color="blue"   />
                <FeatureCard icon={Users}       title="Mise en relation"     desc="Entrez en contact avec les porteurs de projets qui vous intéressent."                  color="green"  />
                <FeatureCard icon={TrendingUp}  title="Visibilité"           desc="Augmentez la visibilité de votre projet auprès de l'écosystème entrepreneurial."        color="orange" />
              </div>
              <InfoBox type="info">
                La page publique des projets <strong>/projets</strong> est accessible sans connexion, ce qui permet à n'importe quel visiteur de découvrir les idées publiées.
              </InfoBox>
            </section>

            {/* ══ MARKETPLACE ═══════════════════════════════════════ */}
            <section>
              <SectionTitle id="marketplace" icon={ShoppingBag} color="orange">Marketplace</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Gratuit" />
                <span className="text-xs text-gray-400">(accès limité) ·</span>
                <PlanBadge plan="Pro Max" />
                <span className="text-xs text-gray-400">(produits illimités)</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                La Marketplace est une place de marché B2B/B2C où les entrepreneurs achètent et vendent
                des services, formations, templates, outils SaaS et produits.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <FeatureCard icon={ShoppingBag} title="20+ catégories"        desc="Formation, SaaS, Templates, Consulting, Design, Dev, Marketing, AI, Agro, Mode…"        color="orange" />
                <FeatureCard icon={DollarSign}  title="Modèles de prix flexibles" desc="Tarif fixe, tarif horaire ou prix sur devis selon le type de prestation."           color="green"  />
                <FeatureCard icon={Star}        title="Avis & Évaluations"    desc="Système de notation vendeurs/produits pour garantir la qualité des échanges."           color="yellow" />
                <FeatureCard icon={MessageSquare} title="Messagerie intégrée" desc="Communiquez directement avec acheteurs ou vendeurs depuis la plateforme."               color="blue"   />
                <FeatureCard icon={Globe}       title="Multi-devise"          desc="EUR, XOF, USD — adaptez vos prix à votre marché cible."                                 color="teal"   />
                <FeatureCard icon={Award}       title="Badges vendeur"        desc="Les vendeurs actifs et bien notés obtiennent des badges de confiance."                  color="purple" />
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Catégories disponibles</h3>
              <div className="flex flex-wrap gap-2">
                {['Formation', 'SaaS & Outils', 'Templates', 'Services', 'Consulting', 'Design', 'Développement', 'Marketing', 'IA & Automation', 'Agroalimentaire', 'Mode', 'Santé', 'Immobilier', 'Finance', 'Agriculture', 'Transport', 'Énergie', 'Artisanat'].map(c => (
                  <span key={c} className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400 shadow-sm">
                    {c}
                  </span>
                ))}
              </div>
            </section>

            {/* ══ COACHING ══════════════════════════════════════════ */}
            <section>
              <SectionTitle id="coaching" icon={Target} color="rose">Coaching</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <PlanBadge plan="Pro" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Le module Coaching offre un accompagnement structuré et personnalisé par des experts.
                Il est conçu pour les entrepreneurs qui souhaitent progresser avec un suivi individuel mesurable.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FeatureCard icon={Target}    title="Plans d'accompagnement" desc="Plans structurés avec axes de développement, objectifs mesurables et indicateurs de progression." color="orange" />
                <FeatureCard icon={Clock}     title="Séances de coaching"   desc="Gestion des rendez-vous, compte-rendus de séance et suivi de l'avancement des objectifs."    color="blue"   />
                <FeatureCard icon={BarChart2} title="Dashboard coaching"    desc="Vue analytique de votre progression avec indicateurs clés sur la durée."                     color="purple" />
                <FeatureCard icon={FileText}  title="Compte-rendus"         desc="Les compte-rendus sont archivés et consultables à tout moment pour assurer la continuité."    color="green"  />
              </div>
              <InfoBox type="info">
                L'accès au coaching peut être initié par vous-même ou par un coach qui vous envoie une invitation. Contactez votre administrateur ou votre organisme de formation pour en savoir plus.
              </InfoBox>
            </section>

            {/* ══ TRANSVERSAL FEATURES ═══════════════════════════════ */}
            <section>
              <SectionTitle id="transversal" icon={Cpu} color="blue">Fonctionnalités communes</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Ces fonctionnalités sont disponibles dans plusieurs modules simultanément.
              </p>
              <div className="space-y-4">
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center">
                      <Brain size={18} />
                    </div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Assistants IA intégrés</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    Chaque module dispose d'un assistant IA spécialisé : IA MOOC pour la formation, IA Gestion
                    pour les questions comptables, IA AgroGestion pour les conseils agricoles, IA Kit360° pour
                    l'aide stratégique. Ils sont accessibles directement depuis chaque module.
                  </p>
                </div>
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center">
                      <Users2 size={18} />
                    </div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Collaboration & espaces partagés</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    Dans Gestion et AgroGestion, le propriétaire du compte peut inviter des collaborateurs via
                    un lien d'invitation (<code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">/gestion/rejoindre</code> ou <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">/agro/rejoindre</code>).
                    Les collaborateurs accèdent en lecture seule par défaut.
                  </p>
                </div>
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-xl flex items-center justify-center">
                      <Bell size={18} />
                    </div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Notifications centralisées</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Les notifications de tous les modules remontent dans un centre de notifications unifié.
                    Vous pouvez filtrer par type, priorité et module, et marquer comme lu individuellement ou en masse.
                  </p>
                </div>
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center">
                      <Globe size={18} />
                    </div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Multi-devise & internationalisation</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Tous les modules financiers (Gestion, AgroGestion, Marketplace, Business Plan) supportent
                    l'Euro (EUR), le Franc CFA (XOF) et le Dollar US (USD). La devise se configure dans les
                    paramètres de chaque module de manière indépendante.
                  </p>
                </div>
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-xl flex items-center justify-center">
                      <Headphones size={18} />
                    </div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Test entrepreneurial</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Disponible gratuitement sur <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">/test-entrepreneurial</code>,
                    ce profiling mesure votre aptitude à l'entrepreneuriat et vous oriente vers les ressources
                    les plus adaptées à votre profil.
                  </p>
                </div>
              </div>
            </section>

            {/* ══ SUBSCRIPTION ════════════════════════════════════════ */}
            <section>
              <SectionTitle id="subscription" icon={CreditCard} color="green">Abonnements</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Choisissez le plan adapté à votre stade de développement. Vous pouvez évoluer ou résilier à tout moment.
              </p>
              <SubscriptionTable />
              <InfoBox type="info">
                Les paiements sont traités via <strong>Stripe</strong>. Aucune donnée bancaire n'est stockée sur les serveurs Innovactive360°.
              </InfoBox>
              <InfoBox type="warning">
                En cas de résiliation, votre accès reste actif jusqu'à la fin de la période payée. Vos données sont conservées et disponibles si vous vous réabonnez ultérieurement.
              </InfoBox>
            </section>

            {/* ══ PROFILE & SETTINGS ══════════════════════════════════ */}
            <section>
              <SectionTitle id="profile" icon={Settings} color="blue">Profil & Paramètres</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Gérez vos informations personnelles et personnalisez votre expérience depuis votre espace personnel.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Users size={15} className="text-blue-500" /> Profil
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['Nom & prénom', 'Photo de profil', 'Email', 'Téléphone', 'Localisation', 'Biographie'].map(i => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle size={12} className="text-green-500 flex-shrink-0" /> {i}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Settings size={15} className="text-purple-500" /> Paramètres
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['Thème clair / sombre', 'Langue de l\'interface', 'Devise préférée', 'Notifications (email, push)', 'Confidentialité', 'Changement de mot de passe'].map(i => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle size={12} className="text-green-500 flex-shrink-0" /> {i}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* ══ SECURITY ════════════════════════════════════════════ */}
            <section>
              <SectionTitle id="security" icon={Lock} color="orange">Sécurité</SectionTitle>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                La sécurité de votre compte et de vos données est une priorité absolue sur Innovactive360°.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <FeatureCard icon={Shield}    title="Auth sécurisée"       desc="Sessions via Supabase Auth avec tokens JWT à durée limitée et renouvellement automatique."  color="green"  />
                <FeatureCard icon={Lock}      title="CAPTCHA"              desc="Cloudflare Turnstile sur les formulaires d'inscription et de connexion pour bloquer les bots." color="blue"   />
                <FeatureCard icon={Globe}     title="HTTPS obligatoire"    desc="Toutes les communications sont chiffrées en transit (TLS 1.3) sans exception."               color="purple" />
                <FeatureCard icon={Shield}    title="Contrôle d'accès"     desc="Accès aux modules strictement limité selon votre plan et rôle. Aucun contournement possible."  color="orange" />
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">Bonnes pratiques</h3>
              <InfoBox type="success">
                <strong>Mot de passe fort :</strong> minimum 12 caractères avec majuscules, chiffres et symboles.
              </InfoBox>
              <InfoBox type="info">
                <strong>Mot de passe unique :</strong> n'utilisez pas le même mot de passe que sur d'autres sites. Utilisez un gestionnaire de mots de passe.
              </InfoBox>
              <InfoBox type="warning">
                <strong>Déconnexion :</strong> déconnectez-vous toujours après utilisation sur un appareil partagé ou public.
              </InfoBox>
              <InfoBox type="danger">
                <strong>Phishing :</strong> l'équipe Innovactive360° ne vous demandera <em>jamais</em> votre mot de passe par email, chat ou téléphone. Signalez immédiatement tout message suspect.
              </InfoBox>
            </section>

            {/* ══ FAQ ══════════════════════════════════════════════════ */}
            <section>
              <SectionTitle id="faq" icon={HelpCircle} color="blue">Foire aux questions</SectionTitle>
              {[
                { q: "Comment accéder à un module Pro sans abonnement ?", a: "Les modules Pro (Gestion, AgroGestion, Kit360°, Business Plan complet, Coaching) nécessitent un abonnement payant. Rendez-vous dans Abonnements pour choisir votre plan. Un essai peut être disponible selon les offres en cours." },
                { q: "Mes données de Gestion et d'AgroGestion sont-elles séparées ?", a: "Oui, Gestion et AgroGestion sont deux espaces distincts avec des données indépendantes. Chaque module a son propre espace de travail, mais partagent la même authentification." },
                { q: "Comment inviter un collaborateur sur mon espace Gestion ?", a: "Dans le module Gestion, rendez-vous dans la section Collaboration. Générez un lien d'invitation et partagez-le. Le collaborateur rejoindra via /gestion/rejoindre avec un accès en lecture seule par défaut." },
                { q: "Le Business Plan s'ouvre-t-il sur un autre site ?", a: "Le Business Plan s'ouvre dans un nouvel onglet de votre navigateur. Votre session est automatiquement transmise de manière sécurisée. Si le popup est bloqué, autorisez les popups pour innovactive360.cloud dans les paramètres de votre navigateur." },
                { q: "Puis-je utiliser plusieurs devises dans le même module ?", a: "Chaque module financier utilise une devise à la fois, configurable dans ses paramètres. Vous pouvez utiliser EUR dans Gestion et XOF dans AgroGestion simultanément." },
                { q: "Le Kit360° fonctionne-t-il hors-ligne ?", a: "Non, le Kit360° nécessite une connexion internet. Vos données sont sauvegardées dans le cloud et accessibles depuis n'importe quel appareil connecté." },
                { q: "Comment obtenir un certificat MOOC ?", a: "Terminez toutes les leçons du cours et réussissez l'évaluation finale avec le score minimum requis. Le certificat est généré automatiquement et disponible dans la section Certificats, téléchargeable en PDF et partageable sur LinkedIn." },
                { q: "Comment réinitialiser mon mot de passe ?", a: "Depuis la page de connexion, cliquez sur «Mot de passe oublié». Entrez votre email, vous recevrez un lien valable 1 heure pour créer un nouveau mot de passe." },
                { q: "Comment publier sur la Marketplace ?", a: "Créez votre annonce depuis le module Marketplace : titre, description, prix, devise, catégorie et délai de livraison. Le plan Gratuit permet un accès limité ; le plan Pro Max permet des publications illimitées." },
                { q: "Que se passe-t-il si je résilie mon abonnement ?", a: "Votre accès reste actif jusqu'à la fin de la période payée. Vos données (gestion, AgroGestion, etc.) sont conservées. Si vous vous réabonnez, vous retrouvez tout votre historique intact." },
              ].map(({ q, a }) => (
                <FAQItem key={q} question={q} answer={a} />
              ))}
            </section>

            {/* ── Footer CTA ── */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white text-center shadow-xl">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe size={28} />
              </div>
              <h3 className="text-2xl font-black mb-2">Prêt à développer votre projet ?</h3>
              <p className="text-blue-100 mb-6 text-sm max-w-md mx-auto">
                Rejoignez plus de 10 000 entrepreneurs qui utilisent Innovactive360° pour gérer, former et développer leur activité.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/register"
                  className="flex items-center gap-2 bg-white text-blue-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow">
                  <UserPlus size={16} /> Créer un compte gratuit
                </Link>
                <Link to="/subscription"
                  className="flex items-center gap-2 border border-white/40 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-white/10 transition-colors">
                  <CreditCard size={16} /> Voir les abonnements
                </Link>
              </div>
            </div>

            <div className="pb-8 text-center text-xs text-gray-400 dark:text-gray-600">
              Documentation Innovactive360° · Dernière mise à jour : mai 2026 ·{' '}
              <Link to="/privacy" className="hover:text-blue-500 transition-colors">Confidentialité</Link>
              {' · '}
              <Link to="/terms" className="hover:text-blue-500 transition-colors">Conditions d'utilisation</Link>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
}
